<div class="suburb-place">
  <div *ngIf="loading && !place" class="text-600">Loading…</div>
  <div *ngIf="error" class="err mb-2">{{ error }}</div>

  <ng-container *ngIf="place as p">
    <section>
      <div class="place-name">
        <ng-container *ngIf="renaming === null; else renameForm">
          <!-- The panel header shows the name; renaming happens here. -->
          <button *ngIf="!readOnly && !p.provider" pButton type="button" icon="pi pi-pencil" label="Rename" class="p-button-text p-button-sm px-0 place-rename-btn" (click)="startRename()"></button>
        </ng-container>
        <ng-template #renameForm>
          <div class="p-inputgroup">
            <input pInputText type="text" class="place-rename" [(ngModel)]="renaming" (keydown.enter)="saveName()" maxlength="200" />
            <button pButton type="button" label="Save" (click)="saveName()" [disabled]="busy"></button>
            <button pButton type="button" class="p-button-text" label="Cancel" (click)="renaming = null"></button>
          </div>
          <div class="meta">Our name for it in Conductor and on the website. Clear it to go back to the crawl's name ("{{ p.crawlName }}").</div>
        </ng-template>
      </div>
      <div class="text-600 text-sm">
        {{ kindLabel(p.kind) }}<span *ngIf="addressLine"> · {{ addressLine }}</span>
      </div>
      <div *ngIf="p.displayName && !p.custom" class="meta">Crawl name: {{ p.crawlName }}</div>
      <div *ngIf="p.custom" class="meta custom-note">
        <i class="pi pi-user-edit" style="font-size: 0.75rem"></i> Added by hand{{ p.custom.createdAt ? ', ' + (p.custom.createdAt | date: 'd MMM y') : '' }}.
        <a *ngIf="!readOnly" class="cursor-pointer edit-custom" (click)="editCustom.emit(p)">Edit details or move the pin</a>
      </div>
      <div class="mt-1"><span class="place-status" [class]="'ps-' + p.status">{{ statusLabel(p.status) }}</span></div>
    </section>

    <section class="place-website">
      <div class="lbl">Website</div>
      <ng-container [ngSwitch]="p.listing">
        <div *ngSwitchCase="'listed'"><strong class="ok">On the website</strong></div>
        <div *ngSwitchCase="'pending'">
          <strong class="warn">Waiting for review</strong>
          <span *ngIf="p.request?.requestedAt" class="text-600 text-sm"> · {{ p.request?.source === 'ai' ? 'suggested' : 'asked' }} {{ p.request?.requestedAt | date: 'd MMM, h:mm a' }}</span>
          <!-- Suggested by the matching pipeline's AI (#261): never listed until a person approves it. -->
          <div *ngIf="p.request?.source === 'ai'" class="ai-request mt-1">
            <span class="ai-tag">AI suggested</span><span *ngIf="p.request?.aiModel" class="text-500 text-xs"> {{ p.request?.aiModel }}</span>
            <div *ngIf="p.request?.note" class="ai-note">{{ p.request?.note }}</div>
          </div>
        </div>
        <div *ngSwitchDefault class="text-600">Not on the website</div>
      </ng-container>
      <div *ngIf="!readOnly" class="flex flex-wrap gap-2 mt-2">
        <button *ngIf="p.listing !== 'listed' && (p.provider || (p.listable && p.status !== 'hidden'))" pButton type="button" class="p-button-sm p-button-success place-list"
                icon="pi pi-globe" label="Add to website" (click)="listing('add')" [disabled]="busy"
                [pTooltip]="p.provider ? 'Takes the provider\'s &quot;Hide From Website&quot; tag off' : 'On the website from the next publish of the suburbs near it'"></button>
        <ng-container *ngIf="!p.provider && p.listing === 'pending'">
          <button pButton type="button" class="p-button-sm p-button-outlined place-approve" icon="pi pi-check" label="Approve" (click)="listing('approve')" [disabled]="busy"></button>
          <button pButton type="button" class="p-button-sm p-button-outlined p-button-danger" icon="pi pi-times" label="Reject" (click)="listing('reject')" [disabled]="busy"></button>
        </ng-container>
        <button *ngIf="p.listing === 'listed'" pButton type="button" class="p-button-sm p-button-outlined p-button-danger" icon="pi pi-eye-slash" label="Remove from website" (click)="listing('remove')" [disabled]="busy"></button>
      </div>
      <div *ngIf="!p.provider && !p.listable" class="meta">Suburb pages list crematoria, cemeteries and places for a service or a wake; this kind of place isn't one.</div>
      <div *ngIf="!p.provider && p.status === 'hidden'" class="meta">Hidden in curation ({{ p.curation?.reason }}): it can't go on the website.</div>
      <div *ngIf="message" class="ok-msg mt-2">{{ message }}</div>
    </section>

    <section>
      <div class="lbl">Conductor</div>
      <ng-container *ngIf="p.provider; else notProvider">
        <div>{{ p.provider.name || 'Provider #' + p.provider.serviceId }} <span class="text-600 text-sm">· used {{ p.provider.usedCount }} time{{ p.provider.usedCount === 1 ? '' : 's' }}</span></div>
        <a *ngIf="canOpenProviders" class="text-sm" [href]="'#/dashboard/service-providers/locations?serviceId=' + p.provider.serviceId" target="_blank" rel="noopener">Open the provider's locations</a>
        <div *ngIf="p.status === 'retired'" class="meta">Not offered for new bookings.</div>
        <div *ngIf="!readOnly && canManageConductor" class="flex flex-wrap gap-2 mt-2">
          <button *ngIf="p.status === 'retired'" pButton type="button" class="p-button-sm p-button-outlined" label="Use for new bookings again" (click)="conductorOut('unretire')" [disabled]="busy"></button>
          <button *ngIf="p.status !== 'retired' && !p.providerRefs" pButton type="button" class="p-button-sm p-button-outlined p-button-danger place-remove-conductor" icon="pi pi-trash" label="Remove from Conductor" (click)="conductorOut('remove')" [disabled]="busy"></button>
          <button *ngIf="p.status !== 'retired' && p.providerRefs" pButton type="button" class="p-button-sm p-button-outlined p-button-danger" icon="pi pi-ban" label="Stop using for new bookings" (click)="conductorOut('retire')" [disabled]="busy"></button>
        </div>
        <div *ngIf="p.status !== 'retired' && p.providerRefs" class="meta">On {{ p.providerRefs }} arrangement{{ p.providerRefs === 1 ? '' : 's' }}, so it can't be removed from Conductor; it can stop being offered for new bookings.</div>
      </ng-container>
      <ng-template #notProvider>
        <div class="text-600 text-sm">Not a provider.</div>
        <ng-container *ngIf="p.removedProvider">
          <div class="meta">It was in Conductor as "{{ p.removedProvider.name || 'provider #' + p.removedProvider.serviceId }}" and was removed.</div>
          <button *ngIf="!readOnly && canManageConductor" pButton type="button" class="p-button-sm mt-2 place-restore" icon="pi pi-replay" label="Restore to Conductor" (click)="restore()" [disabled]="busy"></button>
        </ng-container>
        <div *ngIf="!readOnly && !adding && !p.removedProvider" class="flex align-items-center gap-2 mt-2">
          <button pButton type="button" class="p-button-sm place-add" icon="pi pi-plus" [label]="'Add to Conductor as ' + blockLabel[addAs]" (click)="startAdd()" [disabled]="busy"></button>
          <p-dropdown [options]="blockTypes" [(ngModel)]="addAs" optionLabel="label" optionValue="value" styleClass="p-inputtext-sm" appendTo="body" pTooltip="Add as"></p-dropdown>
        </div>
        <div *ngIf="adding as a" class="place-adding mt-2">
          <div *ngIf="!a.matches" class="text-600 text-sm">Checking for providers it may already be…</div>
          <ng-container *ngIf="a.matches?.length">
            <div class="text-sm mb-1">It may already be in Conductor. Dismiss each one that isn't, or link it from the provider's Locations page instead:</div>
            <ul class="plain">
              <li *ngFor="let m of a.matches" class="flex align-items-center justify-content-between gap-2">
                <span>{{ m.name }} <span class="text-600 text-xs">({{ m.why.join(', ') }})</span></span>
                <span *ngIf="m.hard" class="warn text-sm">Already this provider</span>
                <button *ngIf="!m.hard" pButton type="button" class="p-button-sm p-button-text" [label]="a.dismissed.has(m.serviceId) ? 'Not this one ✓' : 'Not this one'" (click)="toggleDismiss(m)"></button>
              </li>
            </ul>
            <div class="flex gap-2 mt-2">
              <button pButton type="button" class="p-button-sm" [label]="'Add anyway as ' + blockLabel[a.blockType]" (click)="add()" [disabled]="!canAdd"></button>
              <button pButton type="button" class="p-button-sm p-button-text" label="Cancel" (click)="adding = null"></button>
            </div>
          </ng-container>
          <div class="meta">A new provider starts tagged "Hide From Website", visible to our account only.</div>
        </div>
      </ng-template>
    </section>

    <section>
      <div class="lbl">Details</div>
      <div class="place-facts text-sm">
        <div *ngIf="p.website"><a [href]="p.website" target="_blank" rel="noopener noreferrer nofollow">{{ p.website }}</a></div>
        <div *ngIf="p.phone"><a [href]="'tel:' + p.phone">{{ p.phone }}</a></div>
        <div *ngIf="p.email"><a [href]="'mailto:' + p.email">{{ p.email }}</a></div>
        <div *ngIf="socials.length" class="flex flex-wrap gap-2">
          <a *ngFor="let s of socials" [href]="s.url" target="_blank" rel="noopener noreferrer nofollow" class="capitalize">{{ s.key }}</a>
        </div>
        <div *ngIf="!p.website && !p.phone && !p.email && !socials.length" class="text-600">The crawl has no contact details for it.</div>
      </div>
      <button pButton type="button" class="p-button-text p-button-sm px-0 mt-1" [label]="more ? 'Less' : 'More details'" (click)="more = !more"></button>
      <div *ngIf="more" class="place-more text-sm">
        <div *ngIf="p.hours"><span class="text-600">Hours:</span> {{ p.hours }}</div>
        <div *ngFor="let l of p.links"><span class="text-600 capitalize">{{ l.label }}:</span> <a [href]="l.url" target="_blank" rel="noopener noreferrer nofollow">{{ l.url }}</a></div>
        <div *ngIf="p.googleMapsUrl"><a [href]="p.googleMapsUrl" target="_blank" rel="noopener noreferrer">Open in Google Maps</a>&nbsp;<span class="text-600">(reviews and photos are Google's: look, don't copy)</span></div>
        <div *ngIf="p.position" class="text-600">{{ p.position.lat | number: '1.5-5' }}, {{ p.position.lng | number: '1.5-5' }} · from {{ p.source === 'osm' ? 'OpenStreetMap' : p.source === 'overture' ? 'Overture Maps' : p.source }}</div>
        <div class="text-600">Photos: our own photos of a place come with the file library.</div>
      </div>
    </section>

    <section>
      <div class="lbl">Notes</div>
      <div *ngFor="let n of p.notes" class="place-note">
        <div>{{ n.body }}</div>
        <div class="meta flex justify-content-between">
          <span>{{ who(n) }} · {{ n.createdAt | date: 'd MMM y' }}</span>
          <a *ngIf="!readOnly" class="cursor-pointer" (click)="deleteNote(n.id)">Delete</a>
        </div>
      </div>
      <div *ngIf="!p.notes.length" class="text-600 text-sm">No notes.</div>
      <div *ngIf="!readOnly" class="mt-2">
        <textarea pInputTextarea class="w-full place-note-input" rows="2" maxlength="2000" [(ngModel)]="noteText" placeholder="A note for the team (about the place, never about people)"></textarea>
        <button pButton type="button" class="p-button-sm mt-1" label="Add note" (click)="addNote()" [disabled]="busy || !noteText.trim()"></button>
      </div>
    </section>

    <section *ngIf="p.history.length">
      <div class="lbl">History</div>
      <ul class="plain text-sm">
        <li *ngFor="let h of p.history">{{ h.at | date: 'd MMM, h:mm a' }} · {{ h.action }}{{ h.detail ? ': ' + h.detail : '' }} · {{ who(h) }}</li>
      </ul>
    </section>
  </ng-container>
</div>
